import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InboxIcon, Phone1Icon, PinLocationIcon } from "@/components/icons/assets";

export default function ContactFormSection() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log(formData);
  };

  return (
    <section id="contact-content" className="px-4 md:px-8 lg:px-20 py-12 sm:py-16 md:py-20 lg:py-24 bg-white">
      {/* Header */}
      <div className="flex flex-col items-center text-center mb-8 sm:mb-12">
        <Badge variant="outline" className="font-plus-jakarta text-xs sm:text-sm font-medium text-neutral-500 rounded-full px-4 py-2 mb-4 sm:mb-6 border border-stroke-light">
          Get Into Touch
        </Badge>
        <h2 className="font-plus-jakarta text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6">
          GET IN TOUCH WITH WHEELTOX
        </h2>
        <p className="font-plus-jakarta text-sm sm:text-base text-neutral-300 max-w-xl">
          Have a question about buying, selling, or shipping wheels? Our team is here to help you every step of the way.
        </p>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto bg-background-50 rounded-xl p-6 sm:p-8 md:p-10 lg:p-12 mb-12 sm:mb-16">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Row 1: Full Name & Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-plus-jakarta text-sm font-medium text-neutral-900 mb-2 block">
                Full Name <span className="text-error">*</span>
              </label>
              <Input
                type="text"
                placeholder="Enter your Name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
                className="font-plus-jakarta bg-white border-none shadow-none h-12 rounded-xl"
              />
            </div>
            <div>
              <label className="font-plus-jakarta text-sm font-medium text-neutral-900 mb-2 block">
                Email <span className="text-error">*</span>
              </label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="font-plus-jakarta bg-white border-none shadow-none h-12 rounded-xl"
              />
            </div>
          </div>

          {/* Row 2: Phone Number & Subject */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-plus-jakarta text-sm font-medium text-neutral-900 mb-2 block">
                Phone Number <span className="text-error">*</span>
              </label>
              <Input
                type="tel"
                placeholder="45 00 00 00 00"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="font-plus-jakarta bg-white border-none shadow-none h-12 rounded-xl"
              />
            </div>
            <div>
              <label className="font-plus-jakarta text-sm font-medium text-neutral-900 mb-2 block">
                Subject <span className="text-error">*</span>
              </label>
              <Select
                value={formData.subject}
                onValueChange={(value) => setFormData({ ...formData, subject: value })}
                required
              >
                <SelectTrigger className="font-plus-jakarta bg-white border-none shadow-none h-12 rounded-xl">
                  <SelectValue placeholder="Buying Wheel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buying">Buying Wheel</SelectItem>
                  <SelectItem value="selling">Selling Wheel</SelectItem>
                  <SelectItem value="shipping">Shipping</SelectItem>
                  <SelectItem value="returns">Returns & Refunds</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Message */}
          <div>
            <label className="font-plus-jakarta text-sm font-medium text-neutral-900 mb-2 block">
              Message <span className="text-error">*</span>
            </label>
            <Textarea
              placeholder="Enter your Message Here"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              className="font-plus-jakarta bg-white border-none shadow-none rounded-xl min-h-[150px] resize-none"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="font-plus-jakarta font-bold bg-primary hover:bg-primary/90 text-white h-12 px-8 rounded-xl cursor-pointer"
          >
            Send Message
          </Button>
        </form>
      </div>

      {/* Contact Info */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
        {/* Phone */}
        <div className="text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
            <Phone1Icon className="" />
            <h3 className="font-plus-jakarta text-sm sm:text-base font-semibold text-neutral-900">Phone Number</h3>
          </div>
          <p className="font-plus-jakarta text-sm sm:text-base text-neutral-300">+1 (706) 886-1494</p>
        </div>

        {/* Email */}
        <div className="text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
            <InboxIcon className="" />
            <h3 className="font-plus-jakarta text-sm sm:text-base font-semibold text-neutral-900">Email</h3>
          </div>
          <p className="font-plus-jakarta text-sm sm:text-base text-neutral-300">question@wheeltox.com</p>
        </div>

        {/* Address */}
        <div className="text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
            <PinLocationIcon className="" />
            <h3 className="font-plus-jakarta text-sm sm:text-base font-semibold text-neutral-900">Address</h3>
          </div>
          <p className="font-plus-jakarta text-sm sm:text-base text-neutral-300">Gladsaxe Møllevej 21, 2860 Søborg</p>
        </div>
      </div>
    </section>
  );
}
